import { useState, useEffect, useCallback } from "react";
import {
  Search,
  X,
  Loader2,
  Package,
  Truck,
  User,
  Calendar,
  FileText,
  ChevronDown,
  ChevronUp,
  ArrowLeft,
} from "lucide-react";
import { useOrderSearchStore } from "./order-search-store";
import { useOrderSearch } from "@/api/orders";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";

interface OrderSearchDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export function OrderSearchDialog({ isOpen, onClose }: OrderSearchDialogProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [expandedCards, setExpandedCards] = useState<Record<string, boolean>>(
    {}
  );

  // Get functions from the global search store
  const { searchedOrderNumber, setSearchedOrderNumber } = useOrderSearchStore();

  // Use the order search hook
  const { data: searchResponse, isLoading: isSearching } =
    useOrderSearch(debouncedQuery);

  const searchResults = searchResponse?.data ?? [];

  const toggleCardExpansion = (orderId: string) => {
    setExpandedCards((prev) => ({
      ...prev,
      [orderId]: !prev[orderId],
    }));
  };

  const isCardExpanded = (orderId: string) => {
    return !!expandedCards[orderId];
  };

  // Focus input when dialog opens and optionally pre-fill with current search
  useEffect(() => {
    if (isOpen) {
      const searchInput = document.getElementById("full-screen-search-input");
      if (searchInput) {
        searchInput.focus();

        // Pre-fill with current search if one exists
        if (searchedOrderNumber && searchQuery === "") {
          setSearchQuery(searchedOrderNumber);
          setDebouncedQuery(searchedOrderNumber);
        }
      }
    }
  }, [isOpen, searchedOrderNumber, searchQuery]);

  // Debounce search query
  useEffect(() => {
    // Remove automatic debounced search
    // Only search will be triggered by form submission now
  }, [searchQuery]);

  const handleOrderSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setDebouncedQuery(searchQuery);
  };

  const handleSelectOrder = (orderNumber: string) => {
    setSearchedOrderNumber(orderNumber);
    onClose();
  };

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    },
    [isOpen, onClose]
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  if (!isOpen) return null;

  const getStatusStyle = (status: string) => {
    switch (status) {
      case "PENDING":
        return "bg-yellow-100 text-yellow-800";
      case "IN_PROGRESS":
        return "bg-blue-100 text-blue-800";
      case "DELIVERED":
        return "bg-green-100 text-green-800";
      case "COMPLETED":
        return "bg-green-100 text-green-800";
      case "CANCELLED":
      case "FAILED":
        return "bg-red-100 text-red-800";
      case "PARTIALLY_DELIVERED":
        return "bg-orange-100 text-orange-800";
      case "OUT_FOR_DELIVERY":
        return "bg-violet-100 text-violet-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl bg-white rounded-md shadow-lg overflow-hidden flex flex-col max-h-[85vh]"
        onClick={(e) => e.stopPropagation()} // Prevent clicks inside the dialog from closing it
      >
        {/* Search Header */}
        <div className="flex-shrink-0 p-4">
          <form onSubmit={handleOrderSearch} className="relative">
            <div className="relative flex items-center">
              <div className="absolute left-3 flex items-center pointer-events-none">
                <Search className="text-gray-400" size={18} />
              </div>
              <Input
                id="full-screen-search-input"
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-28 py-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
              <div className="absolute right-3 text-sm text-gray-400 flex items-center">
                Press{" "}
                <span className="mx-1 px-1.5 py-0.5 bg-gray-100 border border-gray-300 rounded flex items-center">
                  <ArrowLeft size={12} className="rotate-180" />
                </span>{" "}
                to search
              </div>
            </div>
          </form>

          {!searchResults.length && !isSearching && searchQuery && (
            <div className="mt-20 flex flex-col items-center justify-center">
              <div className="bg-gray-100 p-6 rounded-full mb-4">
                <Search className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="text-base font-medium text-gray-900 mb-1">
                Search for orders
              </h3>
              <p className="text-sm text-gray-500 text-center">
                Start typing to search by order number, tracking number,
                customer name, or phone
              </p>
            </div>
          )}

          {!searchResults.length && !isSearching && !searchQuery && (
            <div className="mt-20 flex flex-col items-center justify-center">
              <div className="bg-gray-100 p-6 rounded-full mb-4">
                <Search className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="text-base font-medium text-gray-900 mb-1">
                Search for orders
              </h3>
              <p className="text-sm text-gray-500 text-center">
                Start typing to search by order number, tracking number,
                customer name, or phone
              </p>
            </div>
          )}
        </div>

        {searchResults.length > 0 && (
          <div className="border-t border-b border-gray-200 px-4 py-2 bg-gray-50">
            <p className="text-sm text-gray-500">
              {searchResults.length} result
              {searchResults.length !== 1 ? "s" : ""} found
            </p>
          </div>
        )}

        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500"
        >
          <X className="h-5 w-5" />
        </Button>

        {/* Results Section */}
        {searchResults.length > 0 && (
          <div className="overflow-y-auto flex-grow">
            <div className="p-3">
              {isSearching ? (
                <div className="flex justify-center items-center py-12">
                  <Loader2 size={32} className="animate-spin text-blue-600" />
                </div>
              ) : (
                <div className="space-y-3">
                  {searchResults.map((order) => (
                    <Card
                      key={order.id}
                      className="border-gray-200 shadow-sm hover:shadow transition-shadow"
                    >
                      {/* Order Header */}
                      <CardHeader className="bg-gray-50 border-b border-gray-200 p-4 flex flex-row justify-between items-center">
                        <div
                          className="flex items-center gap-3 cursor-pointer"
                          onClick={() => handleSelectOrder(order.orderNumber)}
                        >
                          <FileText className="h-5 w-5 text-gray-400" />
                          <div className="flex items-center gap-2">
                            <CardTitle className="text-base font-bold">
                              #{order.orderNumber}
                            </CardTitle>
                            <Badge className={getStatusStyle(order.status)}>
                              {order.status.replace(/_/g, " ")}
                            </Badge>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="flex items-center text-sm text-gray-500">
                            <Calendar className="h-4 w-4 mr-1 text-gray-400" />
                            {new Date(order.createdAt).toLocaleString()}
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => toggleCardExpansion(order.id)}
                            className="h-6 w-6 p-0 text-gray-500 hover:bg-gray-100 rounded"
                          >
                            {isCardExpanded(order.id) ? (
                              <ChevronUp className="h-4 w-4" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </CardHeader>

                      {/* Order Basic Info (always visible) */}
                      <CardContent className="p-0">
                        <div className="grid grid-cols-2 border-b border-gray-200">
                          {/* Sender Information */}
                          <div className="p-3 border-r border-gray-200">
                            <div className="flex items-center gap-1.5 text-gray-700 mb-1">
                              <User size={14} className="text-gray-500" />
                              <h4 className="text-xs font-semibold uppercase text-gray-500">
                                Sender
                              </h4>
                            </div>
                            <div className="space-y-1 ml-5">
                              {order.senderName && (
                                <div className="text-sm font-medium">
                                  {order.senderName}
                                </div>
                              )}
                              {order.senderPhone && (
                                <div className="text-xs text-gray-600">
                                  {order.senderPhone}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Recipient Information */}
                          <div className="p-3">
                            <div className="flex items-center gap-1.5 text-gray-700 mb-1">
                              <User size={14} className="text-gray-500" />
                              <h4 className="text-xs font-semibold uppercase text-gray-500">
                                Recipient
                              </h4>
                            </div>
                            <div className="space-y-1 ml-5">
                              {order.recipientName && (
                                <div className="text-sm font-medium">
                                  {order.recipientName}
                                </div>
                              )}
                              {order.recipientPhone && (
                                <div className="text-xs text-gray-600">
                                  {order.recipientPhone}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Expanded Details */}
                        {isCardExpanded(order.id) &&
                          order.parcels &&
                          order.parcels.length > 0 && (
                            <div className="px-4 py-3">
                              <div className="flex items-center gap-1.5 text-gray-700 mb-2">
                                <Package size={14} className="text-gray-500" />
                                <h4 className="text-xs font-semibold uppercase text-gray-500">
                                  Parcels ({order.parcels.length})
                                </h4>
                              </div>

                              <div className="space-y-3">
                                {order.parcels.map((parcel) => (
                                  <div
                                    key={parcel.id}
                                    className="border border-gray-200 rounded-md bg-gray-50"
                                  >
                                    <div className="flex items-center justify-between p-2 border-b border-gray-200 bg-gray-100">
                                      <span className="font-medium text-xs flex items-center">
                                        <Package
                                          size={12}
                                          className="text-gray-400 mr-1"
                                        />
                                        {parcel.trackingNumber}
                                      </span>
                                      <Badge
                                        className={getStatusStyle(
                                          parcel.status
                                        )}
                                      >
                                        {parcel.status.replace(/_/g, " ")}
                                      </Badge>
                                    </div>

                                    {/* Tasks and Trips */}
                                    {parcel.tasks &&
                                      parcel.tasks.length > 0 && (
                                        <div className="divide-y divide-gray-200">
                                          {parcel.tasks.map((task) => (
                                            <div key={task.id} className="p-2">
                                              <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                  <div className="text-xs uppercase font-medium text-gray-700">
                                                    {task.type?.replace(
                                                      /_/g,
                                                      " "
                                                    )}
                                                  </div>
                                                  {task.status && (
                                                    <Badge
                                                      className={`text-xs ${getStatusStyle(task.status)}`}
                                                    >
                                                      {task.status.replace(
                                                        /_/g,
                                                        " "
                                                      )}
                                                    </Badge>
                                                  )}
                                                </div>
                                              </div>
                                              {task.trip && (
                                                <div className="flex items-center gap-1 text-xs text-gray-600 mt-1">
                                                  <Truck
                                                    size={12}
                                                    className="text-gray-400"
                                                  />
                                                  <span>
                                                    Trip #{task.trip.tripNumber}
                                                  </span>
                                                  {task.waypoint?.sequence !==
                                                    undefined && (
                                                    <span className="text-gray-500">
                                                      (Sequence:{" "}
                                                      {task.waypoint.sequence})
                                                    </span>
                                                  )}
                                                </div>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
